import React, { useState } from 'react';
import { useMachines, Machine, MachineStatus } from '../context/MachineContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { usePlan } from '../context/PlanContext';
import { LimitBanner, UsageGuard } from '../components/UsageGuard';
import MachineFormModal from '../components/MachineFormModal';
import SensorsPanel from '../components/SensorsPanel';
import Toast, { useToast } from '../components/Toast';
import './Machines.css';

const STATUS_COLORS: Record<MachineStatus, string> = {
  active:      'badge-green',
  idle:        'badge-yellow',
  maintenance: 'badge-yellow',
  offline:     'badge-red',
};

const STATUS_ICONS: Record<MachineStatus, string> = {
  active: '🟢', idle: '🟡', maintenance: '🔧', offline: '🔴',
};

const MACHINE_TYPE_ICONS: Record<string, string> = {
  'Industrial Mixer': '⚙️', 'Conveyor': '🔄', 'Packaging Machine': '📦',
  'Pump': '💧', 'Compressor': '🔵', 'Robot Arm': '🦾',
  'CNC Machine': '🔩', 'Other': '🏭',
};

const Machines: React.FC = () => {
  const { machines, addMachine, updateMachine, deleteMachine } = useMachines();
  const { workspace, isDemo, guardWrite } = useWorkspace();
  const { checkMachines } = usePlan();
  const { toasts, push, remove } = useToast();

  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState<MachineStatus | 'ALL'>('ALL');
  const [showModal,    setShowModal]    = useState(false);
  const [editTarget,   setEditTarget]   = useState<Machine | null>(null);
  const [expanded,     setExpanded]     = useState<string | null>(null);
  const [confirmDel,   setConfirmDel]   = useState<string | null>(null);

  const machineCheck = checkMachines();

  const filtered = machines.filter(m => {
    const matchSearch = m.name.toLowerCase().includes(search.toLowerCase()) ||
                        m.location.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'ALL' || m.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const counts = {
    ALL:         machines.length,
    active:      machines.filter(m => m.status === 'active').length,
    idle:        machines.filter(m => m.status === 'idle').length,
    maintenance: machines.filter(m => m.status === 'maintenance').length,
    offline:     machines.filter(m => m.status === 'offline').length,
  };

  const handleAdd = () => {
    if (!guardWrite('Add Machine')) return;
    setEditTarget(null);
    setShowModal(true);
  };

  const handleEdit = (m: Machine) => {
    if (!guardWrite('Edit Machine')) return;
    setEditTarget(m);
    setShowModal(true);
  };

  const handleSave = (data: Omit<Machine, 'machineId' | 'sensors' | 'createdAt'>) => {
    if (editTarget) {
      updateMachine(editTarget.machineId, data);
      push(`Machine "${data.name}" updated`);
    } else {
      const m = addMachine(data);
      if (m) push(`Machine "${m.name}" added`);
    }
    setShowModal(false);
    setEditTarget(null);
  };

  const handleDelete = (machineId: string) => {
    const m = machines.find(x => x.machineId === machineId);
    deleteMachine(machineId);
    setConfirmDel(null);
    if (expanded === machineId) setExpanded(null);
    push(`Machine "${m?.name}" deleted`, 'info');
  };

  const toggleExpand = (machineId: string) =>
    setExpanded(prev => prev === machineId ? null : machineId);

  return (
    <div className="machines-page">
      <Toast toasts={toasts} onRemove={remove} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>⚙️ Machines</h1>
          <p className="page-subtitle">
            {workspace?.name} · {machines.length} machine{machines.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <div className="header-actions">
          {isDemo && <span className="demo-badge">🔍 Demo — read only</span>}
          <UsageGuard check={machineCheck} onAction={handleAdd}>
            <button className="btn-primary">+ Add Machine</button>
          </UsageGuard>
        </div>
      </div>

      {/* Limit banner */}
      <LimitBanner check={machineCheck} label="machines" />

      {/* Filters */}
      <div className="machines-filters card">
        <input
          className="search-input"
          placeholder="🔍 Search machines or locations..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="filter-buttons">
          {(['ALL', 'active', 'idle', 'maintenance', 'offline'] as const).map(s => (
            <button
              key={s}
              className={`filter-btn ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'ALL' ? `All (${counts.ALL})` : `${STATUS_ICONS[s]} ${s} (${counts[s]})`}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="machines-empty card">
          <div className="empty-icon">⚙️</div>
          <h3>{search || statusFilter !== 'ALL' ? 'No machines match your filters' : 'No machines yet'}</h3>
          <p>
            {search || statusFilter !== 'ALL'
              ? 'Try adjusting your search or filter.'
              : 'Add your first machine to start configuring your digital twin.'}
          </p>
          {!search && statusFilter === 'ALL' && !isDemo && (
            <button className="btn-primary" onClick={handleAdd}>+ Add First Machine</button>
          )}
        </div>
      )}

      {/* Machine cards */}
      <div className="machines-list">
        {filtered.map(m => (
          <div key={m.machineId} className={`machine-card card ${expanded === m.machineId ? 'expanded' : ''}`}>
            <div className="machine-card-header" onClick={() => toggleExpand(m.machineId)}>
              <div className="machine-icon">
                {MACHINE_TYPE_ICONS[m.type] || '🏭'}
              </div>
              <div className="machine-info">
                <div className="machine-name">{m.name}</div>
                <div className="machine-meta">
                  <span>{m.type}</span>
                  <span>·</span>
                  <span>📍 {m.location}</span>
                  {m.factoryId && <><span>·</span><span>🏭 {m.factoryId}</span></>}
                </div>
              </div>
              <div className="machine-right">
                <span className={STATUS_COLORS[m.status]}>
                  {STATUS_ICONS[m.status]} {m.status}
                </span>
                <span className="sensor-count">📡 {m.sensors.length} sensor{m.sensors.length !== 1 ? 's' : ''}</span>
                <div className="machine-actions" onClick={e => e.stopPropagation()}>
                  <button className="action-btn" onClick={() => handleEdit(m)} title="Edit">✏️</button>
                  <button className="action-btn danger" onClick={() => setConfirmDel(m.machineId)} title="Delete">🗑️</button>
                </div>
                <span className="expand-chevron">{expanded === m.machineId ? '▲' : '▼'}</span>
              </div>
            </div>

            {expanded === m.machineId && (
              <SensorsPanel
                machineId={m.machineId}
                sensors={m.sensors}
                isReadOnly={isDemo}
                onToast={push}
              />
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit modal */}
      {showModal && (
        <MachineFormModal
          initial={editTarget || undefined}
          workspaceId={workspace?.workspaceId || 'demo'}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
        />
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <div className="modal-overlay" onClick={() => setConfirmDel(null)}>
          <div className="confirm-dialog card" onClick={e => e.stopPropagation()}>
            <h3>🗑️ Delete Machine?</h3>
            <p>This will also remove all sensors attached to this machine. This cannot be undone.</p>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDelete(confirmDel)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Machines;
